import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faListCheck,
  faArrowUp,
  faArrowDown,
  faSort,
  faSearch,
  faFileExport,
  faCheck,
  faChevronLeft,
  faChevronRight,
  faClipboardList,
  faPlus,
  faUserEdit,
  faTimes,
  faSpinner,
  faCalendarAlt,
} from '@fortawesome/free-solid-svg-icons';
import {
  useAllTasks,
  useUpdateTask,
  useCreateTask,
  useReassignTask,
  useBulkReassignTasks,
} from '../../hooks/useHome';
import { useSolicitors } from '../../hooks/useCaseFile';
import { useCaseTypes } from '../../hooks/useCases';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { CATEGORY_CONFIG, CATEGORIES, isTaskCompleted, isTaskOverdue } from '../../utils/tasks';
import type { Task, TaskCategory, CaseDetails, AllTasksData } from '../../types/home';
import { EXPORT_FETCH_LIMIT, DEFAULT_PAGE_SIZE } from '../../constants';
import BulkReassignPanel from '../../components/BulkReassignPanel';
import css from './TasksPage.module.css';

/* ── helpers ── */

type SortDir = 'asc' | 'desc';
type SortField =
  | 'Description'
  | 'CaseRef'
  | 'Category'
  | 'Status'
  | 'Deadline'
  | 'assigned_display_name';

const PAGE_SIZE = DEFAULT_PAGE_SIZE;

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function matchesSearch(task: Task, q: string, cdm: Record<string, CaseDetails>): boolean {
  if (!q) return true;
  const lower = q.toLowerCase();
  const desc = (task.Description ?? '').toLowerCase();
  const ref = (task.CaseRef ?? '').toLowerCase();
  const assignee = (task.assigned_display_name ?? '').toLowerCase();
  const defName = cdm[task.CaseRef ?? '']?.defendant_name?.toLowerCase() ?? '';
  return (
    desc.includes(lower) ||
    ref.includes(lower) ||
    assignee.includes(lower) ||
    defName.includes(lower)
  );
}

/* ── component ── */

export default function TasksPage() {
  const { isSuperAdmin } = usePermissions();
  const { user } = useAuth();
  const currentUserId = user?.sub;

  /* ── state ── */
  const [mainTab, setMainTab] = useState<'active' | 'completed'>('active');
  const [catFilter, setCatFilter] = useState<TaskCategory | 'all'>('all');
  const [viewMode, setViewMode] = useState<'mine' | 'all'>('mine');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('Deadline');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(1);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [advReassignOpen, setAdvReassignOpen] = useState(false);
  const [reassigningTaskId, setReassigningTaskId] = useState<string | null>(null);
  const [reassignUserId, setReassignUserId] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkReassignUserId, setBulkReassignUserId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [caseTypeFilter, setCaseTypeFilter] = useState<string>('');
  const [assignedUserFilter, setAssignedUserFilter] = useState<string>('');

  /* ── create task form ── */
  const [taskForm, setTaskForm] = useState({
    description: '',
    deadline: '',
    category: 'misc' as string,
    assigned_to: '',
    allow_reminders: false,
    reminder_datetime: '',
  });

  /* ── data ── */
  const statusParam = mainTab === 'completed' ? 'completed' : 'active';
  // When superadmin picks a specific user, that overrides viewMode='mine'/'all'.
  // Otherwise: viewMode='mine' → 'me', viewMode='all' → undefined (no user filter).
  const effectiveAssignedTo = assignedUserFilter
    ? assignedUserFilter
    : viewMode === 'mine'
      ? 'me'
      : undefined;
  const { data, isLoading } = useAllTasks({
    page,
    limit: PAGE_SIZE,
    status: statusParam,
    category: catFilter === 'all' ? undefined : catFilter,
    assignedTo: effectiveAssignedTo,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    caseTypeId: caseTypeFilter || undefined,
  });

  const updateTask = useUpdateTask();
  const createTask = useCreateTask();
  const reassignTask = useReassignTask();
  const bulkReassign = useBulkReassignTasks();
  const { data: solicitors = [] } = useSolicitors();
  const { data: caseTypes = [] } = useCaseTypes();

  const tasks = data?.tasks ?? [];
  const total = data?.total ?? 0;
  const cdm = data?.caseDetailsMap ?? {};
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  /* ── client-side search + sort ── */
  const filtered = useMemo(() => {
    let list = tasks.filter((t) => matchesSearch(t, search, cdm));

    list.sort((a, b) => {
      let av: string | number = '';
      let bv: string | number = '';
      switch (sortField) {
        case 'Description':
          av = (a.Description ?? '').toLowerCase();
          bv = (b.Description ?? '').toLowerCase();
          break;
        case 'CaseRef':
          av = (a.CaseRef ?? '').toLowerCase();
          bv = (b.CaseRef ?? '').toLowerCase();
          break;
        case 'Category':
          av = (a.Category ?? 'misc').toLowerCase();
          bv = (b.Category ?? 'misc').toLowerCase();
          break;
        case 'Status':
          av = (a.Status ?? '').toLowerCase();
          bv = (b.Status ?? '').toLowerCase();
          break;
        case 'Deadline':
          av = a.Deadline ? new Date(a.Deadline).getTime() : Infinity;
          bv = b.Deadline ? new Date(b.Deadline).getTime() : Infinity;
          break;
        case 'assigned_display_name':
          av = (a.assigned_display_name ?? '').toLowerCase();
          bv = (b.assigned_display_name ?? '').toLowerCase();
          break;
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [tasks, search, sortField, sortDir, cdm]);

  /* ── category counts from server ── */
  const catCounts = data?.categoryCounts ?? {
    all: 0,
    admin: 0,
    client: 0,
    court: 0,
    defendant: 0,
    misc: 0,
  };

  /* ── handlers ── */
  const handleSort = useCallback((field: SortField) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return field;
      }
      setSortDir('asc');
      return field;
    });
  }, []);

  const handleComplete = useCallback(
    (task: Task) => {
      updateTask.mutate({
        taskID: task.TaskID,
        desc: task.Description,
        deadline: task.Deadline ?? '',
        status: 'Completed',
        category: task.Category,
      });
    },
    [updateTask],
  );

  const handleCreateTask = useCallback(async () => {
    if (!taskForm.description.trim() || !taskForm.deadline) return;
    await createTask.mutateAsync({
      desc: taskForm.description,
      deadline: taskForm.deadline,
      category: taskForm.category,
      assigned_to: taskForm.assigned_to || undefined,
      allow_reminders: taskForm.allow_reminders,
      reminder_datetime: taskForm.reminder_datetime || undefined,
    });
    setCreateModalOpen(false);
    setTaskForm({
      description: '',
      deadline: '',
      category: 'misc',
      assigned_to: '',
      allow_reminders: false,
      reminder_datetime: '',
    });
  }, [createTask, taskForm]);

  const handleReassign = useCallback(
    async (taskId: string) => {
      if (!reassignUserId) return;
      await reassignTask.mutateAsync({ taskId, newUserId: reassignUserId });
      setReassigningTaskId(null);
      setReassignUserId('');
    },
    [reassignTask, reassignUserId],
  );

  const handleBulkReassign = useCallback(async () => {
    if (!bulkReassignUserId || selectedIds.size === 0) return;
    await bulkReassign.mutateAsync({ taskIds: [...selectedIds], newUserId: bulkReassignUserId });
    setSelectedIds(new Set());
    setBulkReassignUserId('');
  }, [bulkReassign, bulkReassignUserId, selectedIds]);

  const toggleSelect = useCallback((taskId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((t) => t.TaskID)));
    }
  }, [filtered, selectedIds.size]);

  const [exporting, setExporting] = useState(false);
  const handleExportCSV = useCallback(async () => {
    setExporting(true);
    try {
      // Fetch ALL tasks matching current filters (no page limit)
      const params = new URLSearchParams();
      params.set('limit', String(EXPORT_FETCH_LIMIT));
      params.set('status', statusParam);
      if (catFilter !== 'all') params.set('category', catFilter);
      if (assignedUserFilter) params.set('assignedTo', assignedUserFilter);
      else if (viewMode === 'mine') params.set('assignedTo', 'me');
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (caseTypeFilter) params.set('caseTypeId', caseTypeFilter);
      const { data: allData } = await api.get<AllTasksData>(`/api/home/tasks/all?${params}`);
      const allTasks = allData?.tasks ?? [];

      const headers = ['Description', 'Case Ref', 'Category', 'Status', 'Assigned To', 'Deadline'];
      const rows = allTasks.map((t) => [
        `"${(t.Description ?? '').replace(/"/g, '""')}"`,
        t.CaseRef ?? '',
        CATEGORY_CONFIG[t.Category ?? 'misc']?.label ?? 'Misc',
        t.Status ?? '',
        t.assigned_display_name ?? '',
        t.Deadline ? new Date(t.Deadline).toISOString().slice(0, 10) : '',
      ]);
      const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tasks-${mainTab}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [
    statusParam,
    catFilter,
    viewMode,
    mainTab,
    dateFrom,
    dateTo,
    assignedUserFilter,
    caseTypeFilter,
  ]);

  /* ── sort icon helper ── */
  const sortIcon = (field: SortField) => {
    if (sortField !== field) return <FontAwesomeIcon icon={faSort} className={css.sortIcon} />;
    return (
      <FontAwesomeIcon
        icon={sortDir === 'asc' ? faArrowUp : faArrowDown}
        className={`${css.sortIcon} ${css.sortActive}`}
      />
    );
  };

  return (
    <div className={css.page}>
      {/* ── Header ── */}
      <div className={css.header}>
        <h1 className={css.title}>
          <FontAwesomeIcon icon={faListCheck} /> Tasks
        </h1>
        <div className={css.headerActions}>
          {isSuperAdmin && (
            <div className={css.viewToggle}>
              <button
                className={`${css.viewToggleBtn} ${viewMode === 'mine' ? css.viewToggleBtnActive : ''}`}
                onClick={() => {
                  setViewMode('mine');
                  setPage(1);
                }}
              >
                My Tasks
              </button>
              <button
                className={`${css.viewToggleBtn} ${viewMode === 'all' ? css.viewToggleBtnActive : ''}`}
                onClick={() => {
                  setViewMode('all');
                  setPage(1);
                }}
              >
                All Tasks
              </button>
            </div>
          )}
          <button className={css.exportBtn} onClick={handleExportCSV} disabled={exporting}>
            <FontAwesomeIcon icon={exporting ? faSpinner : faFileExport} spin={exporting} />{' '}
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          {isSuperAdmin && (
            <button
              className={css.exportBtn}
              onClick={() => setAdvReassignOpen(true)}
              title="Bulk reassign tasks with filters"
            >
              <FontAwesomeIcon icon={faUserEdit} /> Bulk Reassign…
            </button>
          )}
          <button className={css.createBtn} onClick={() => setCreateModalOpen(true)}>
            <FontAwesomeIcon icon={faPlus} /> Create Task
          </button>
        </div>
      </div>

      {/* ── Main Tabs ── */}
      <div className={css.mainTabs}>
        <button
          className={`${css.mainTab} ${mainTab === 'active' ? css.mainTabActive : ''}`}
          onClick={() => {
            setMainTab('active');
            setPage(1);
          }}
        >
          Active
        </button>
        <button
          className={`${css.mainTab} ${mainTab === 'completed' ? css.mainTabActive : ''}`}
          onClick={() => {
            setMainTab('completed');
            setPage(1);
          }}
        >
          Completed
        </button>
      </div>

      {/* ── Category Sub-tabs ── */}
      <div className={css.catTabs}>
        <button
          className={`${css.catTab} ${catFilter === 'all' ? css.catTabActive : ''}`}
          style={
            catFilter === 'all'
              ? { background: 'var(--gray-700)', color: 'var(--text-on-primary)' }
              : {}
          }
          onClick={() => {
            setCatFilter('all');
            setPage(1);
          }}
        >
          All <span className={css.catCount}>({catCounts['all'] ?? 0})</span>
        </button>
        {CATEGORIES.map((cat) => {
          const cfg = CATEGORY_CONFIG[cat];
          const isActive = catFilter === cat;
          return (
            <button
              key={cat}
              className={`${css.catTab} ${isActive ? css.catTabActive : ''}`}
              style={
                isActive
                  ? {
                      background: cfg.color,
                      color: 'var(--text-on-primary)',
                      borderColor: cfg.color,
                    }
                  : {}
              }
              onClick={() => {
                setCatFilter(cat);
                setPage(1);
              }}
            >
              {cfg.label} <span className={css.catCount}>({catCounts[cat] ?? 0})</span>
            </button>
          );
        })}
      </div>

      {/* ── Search / Toolbar ── */}
      <div className={css.toolbar}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 350 }}>
          <FontAwesomeIcon
            icon={faSearch}
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              fontSize: 13,
            }}
          />
          <input
            className={css.searchInput}
            style={{ paddingLeft: 32 }}
            placeholder="Search tasks…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className={css.dateFilter}>
          <FontAwesomeIcon icon={faCalendarAlt} className={css.dateFilterIcon} />
          <input
            type="date"
            className={css.dateInput}
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            title="From date"
          />
          <span className={css.dateFilterSep}>–</span>
          <input
            type="date"
            className={css.dateInput}
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            title="To date"
          />
          {(dateFrom || dateTo) && (
            <button
              className={css.dateFilterClear}
              onClick={() => {
                setDateFrom('');
                setDateTo('');
                setPage(1);
              }}
              title="Clear date filter"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>

        {/* Pipeline filter (all users) */}
        <select
          className={css.searchInput}
          style={{ maxWidth: 200 }}
          value={caseTypeFilter}
          onChange={(e) => {
            setCaseTypeFilter(e.target.value);
            setPage(1);
          }}
          title="Filter by pipeline"
        >
          <option value="">All pipelines</option>
          {caseTypes.map((ct) => (
            <option key={ct.id} value={ct.id}>
              {ct.display_name}
            </option>
          ))}
        </select>

        {/* Assigned-user filter (superadmin only) */}
        {isSuperAdmin && (
          <select
            className={css.searchInput}
            style={{ maxWidth: 220 }}
            value={assignedUserFilter}
            onChange={(e) => {
              setAssignedUserFilter(e.target.value);
              setPage(1);
            }}
            title="Filter by assigned user"
          >
            <option value="">{viewMode === 'mine' ? 'Assigned: me' : 'Any user'}</option>
            {solicitors.map((s) => (
              <option key={s.cognito_user_id} value={s.cognito_user_id}>
                {s.displayName || s.username}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* ── Bulk Action Bar ── */}
      {isSuperAdmin && selectedIds.size > 0 && (
        <div className={css.bulkBar}>
          <span className={css.bulkBarCount}>
            {selectedIds.size} task{selectedIds.size > 1 ? 's' : ''} selected
          </span>
          <div className={css.bulkBarActions}>
            <select
              className={css.reassignSelect}
              value={bulkReassignUserId}
              onChange={(e) => setBulkReassignUserId(e.target.value)}
            >
              <option value="">Reassign to…</option>
              {solicitors.map((s) => (
                <option key={s.cognito_user_id} value={s.cognito_user_id}>
                  {s.displayName || s.username}
                </option>
              ))}
            </select>
            <button
              className={css.createBtn}
              disabled={!bulkReassignUserId || bulkReassign.isPending}
              onClick={handleBulkReassign}
            >
              {bulkReassign.isPending ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faUserEdit} />
              )}{' '}
              Reassign
            </button>
            <button
              className={css.cancelBtn}
              onClick={() => {
                setSelectedIds(new Set());
                setBulkReassignUserId('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <div className={css.tableWrap}>
        <table className={css.table}>
          <thead>
            <tr>
              {isSuperAdmin && (
                <th style={{ width: 36, cursor: 'default' }}>
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && selectedIds.size === filtered.length}
                    onChange={toggleSelectAll}
                  />
                </th>
              )}
              <th onClick={() => handleSort('Description')}>
                Description {sortIcon('Description')}
              </th>
              <th onClick={() => handleSort('CaseRef')}>Case Ref {sortIcon('CaseRef')}</th>
              <th onClick={() => handleSort('Category')}>Category {sortIcon('Category')}</th>
              <th onClick={() => handleSort('Status')}>Status {sortIcon('Status')}</th>
              <th onClick={() => handleSort('assigned_display_name')}>
                Assigned To {sortIcon('assigned_display_name')}
              </th>
              <th onClick={() => handleSort('Deadline')}>Deadline {sortIcon('Deadline')}</th>
              <th style={{ cursor: 'default' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={isSuperAdmin ? 8 : 7} className={css.emptyState}>
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={isSuperAdmin ? 8 : 7} className={css.emptyState}>
                  <FontAwesomeIcon icon={faClipboardList} />
                  <div>No tasks found</div>
                </td>
              </tr>
            )}
            {!isLoading &&
              filtered.map((t) => {
                const cat = t.Category ?? 'misc';
                const cfg = CATEGORY_CONFIG[cat];
                const overdue = isTaskOverdue(t);
                const completed = isTaskCompleted(t.Status);
                return (
                  <tr key={t.TaskID} className={selectedIds.has(t.TaskID) ? css.rowSelected : ''}>
                    {isSuperAdmin && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(t.TaskID)}
                          onChange={() => toggleSelect(t.TaskID)}
                        />
                      </td>
                    )}
                    <td className={css.descCell}>{t.Description}</td>
                    <td>
                      {t.CaseRef ? (
                        <Link to={`/cases/${t.CaseRef}`} className={css.caseLink}>
                          {t.CaseRef}
                        </Link>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={css.categoryBadge}
                        style={{ background: cfg.bg, color: cfg.color }}
                      >
                        {cfg.label}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`${css.statusBadge} ${
                          completed
                            ? css.statusCompleted
                            : overdue
                              ? css.statusOverdue
                              : css.statusInProgress
                        }`}
                      >
                        {completed ? 'Completed' : overdue ? 'Overdue' : t.Status}
                      </span>
                    </td>
                    <td>{t.assigned_display_name ?? '—'}</td>
                    <td style={overdue ? { color: 'var(--error-600)', fontWeight: 600 } : {}}>
                      {fmtDate(t.Deadline)}
                    </td>
                    <td>
                      <div className={css.rowActions}>
                        {!completed && (
                          <button
                            className={css.rowActionBtn}
                            title="Mark completed"
                            onClick={() => handleComplete(t)}
                          >
                            <FontAwesomeIcon icon={faCheck} />
                          </button>
                        )}
                        {!completed &&
                          (isSuperAdmin || t.user_id === currentUserId) &&
                          (reassigningTaskId === t.TaskID ? (
                            <div className={css.reassignInline}>
                              <select
                                className={css.reassignSelect}
                                value={reassignUserId}
                                onChange={(e) => setReassignUserId(e.target.value)}
                              >
                                <option value="">Select user…</option>
                                {solicitors.map((s) => (
                                  <option key={s.cognito_user_id} value={s.cognito_user_id}>
                                    {s.displayName || s.username}
                                  </option>
                                ))}
                              </select>
                              <button
                                className={css.rowActionBtn}
                                title="Confirm reassign"
                                disabled={!reassignUserId || reassignTask.isPending}
                                onClick={() => handleReassign(t.TaskID)}
                              >
                                {reassignTask.isPending ? (
                                  <FontAwesomeIcon icon={faSpinner} spin />
                                ) : (
                                  <FontAwesomeIcon icon={faCheck} />
                                )}
                              </button>
                              <button
                                className={css.rowActionBtn}
                                title="Cancel"
                                onClick={() => {
                                  setReassigningTaskId(null);
                                  setReassignUserId('');
                                }}
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </button>
                            </div>
                          ) : (
                            <button
                              className={css.rowActionBtn}
                              title="Reassign task"
                              onClick={() => setReassigningTaskId(t.TaskID)}
                            >
                              <FontAwesomeIcon icon={faUserEdit} />
                            </button>
                          ))}
                        {t.CaseRef && (
                          <Link
                            to={`/cases/${t.CaseRef}`}
                            className={css.rowActionBtn}
                            title="Open case"
                          >
                            Open
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className={css.paginationBar}>
            <span className={css.paginationInfo}>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </span>
            <div className={css.paginationBtns}>
              <button
                className={css.pageBtn}
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let p: number;
                if (totalPages <= 7) {
                  p = i + 1;
                } else if (page <= 4) {
                  p = i + 1;
                } else if (page >= totalPages - 3) {
                  p = totalPages - 6 + i;
                } else {
                  p = page - 3 + i;
                }
                return (
                  <button
                    key={p}
                    className={`${css.pageBtn} ${p === page ? css.pageBtnActive : ''}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                className={css.pageBtn}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Create Task Modal ── */}
      {createModalOpen && (
        <div className={css.modalOverlay} onClick={() => setCreateModalOpen(false)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h2>Create Task</h2>
              <button className={css.modalClose} onClick={() => setCreateModalOpen(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className={css.modalBody}>
              <div className={css.formGroup}>
                <label>Description *</label>
                <textarea
                  rows={3}
                  className={css.formInput}
                  placeholder="Task description…"
                  value={taskForm.description}
                  onChange={(e) => setTaskForm((p) => ({ ...p, description: e.target.value }))}
                />
              </div>
              <div className={css.formGroup}>
                <label>Assign To</label>
                <select
                  className={css.formInput}
                  value={taskForm.assigned_to}
                  onChange={(e) => setTaskForm((p) => ({ ...p, assigned_to: e.target.value }))}
                >
                  <option value="">Myself</option>
                  {solicitors.map((s) => (
                    <option key={s.cognito_user_id} value={s.cognito_user_id}>
                      {s.displayName || s.username}
                    </option>
                  ))}
                </select>
              </div>
              <div className={css.formGroup}>
                <label>Category</label>
                <select
                  className={css.formInput}
                  value={taskForm.category}
                  onChange={(e) => setTaskForm((p) => ({ ...p, category: e.target.value }))}
                >
                  <option value="admin">Admin</option>
                  <option value="client">Client</option>
                  <option value="court">Court</option>
                  <option value="defendant">Defendant</option>
                  <option value="misc">Miscellaneous</option>
                </select>
              </div>
              <div className={css.formGroup}>
                <label>Deadline *</label>
                <input
                  type="datetime-local"
                  className={css.formInput}
                  value={taskForm.deadline}
                  onChange={(e) => setTaskForm((p) => ({ ...p, deadline: e.target.value }))}
                />
              </div>
              <div className={css.formGroup}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={taskForm.allow_reminders}
                    onChange={(e) =>
                      setTaskForm((p) => ({ ...p, allow_reminders: e.target.checked }))
                    }
                  />
                  Enable Reminder
                </label>
              </div>
              {taskForm.allow_reminders && (
                <div className={css.formGroup}>
                  <label>Reminder Date/Time</label>
                  <input
                    type="datetime-local"
                    className={css.formInput}
                    value={taskForm.reminder_datetime}
                    onChange={(e) =>
                      setTaskForm((p) => ({ ...p, reminder_datetime: e.target.value }))
                    }
                  />
                </div>
              )}
            </div>
            <div className={css.modalFooter}>
              <button className={css.cancelBtn} onClick={() => setCreateModalOpen(false)}>
                Cancel
              </button>
              <button
                className={css.createBtn}
                disabled={
                  !taskForm.description.trim() || !taskForm.deadline || createTask.isPending
                }
                onClick={handleCreateTask}
              >
                {createTask.isPending ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faPlus} />
                )}{' '}
                Create Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Advanced Bulk Reassign Modal (superadmin) ── */}
      {advReassignOpen && (
        <div className={css.modalOverlay} onClick={() => setAdvReassignOpen(false)}>
          <div
            className={css.modal}
            style={{ maxWidth: 880, width: '92%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={css.modalHeader}>
              <h2>Bulk Reassign Tasks</h2>
              <button className={css.modalClose} onClick={() => setAdvReassignOpen(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className={css.modalBody}>
              <BulkReassignPanel />
            </div>
            <div className={css.modalFooter}>
              <button className={css.cancelBtn} onClick={() => setAdvReassignOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
